import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import PageHeader from "components/PageLayout";
import EmptyState from "components/EmptyState";
import Skeleton from "components/Skeleton";
import ConfirmDialog from "components/ConfirmDialog";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeApiRequest } from "constants/apiRoutes";
import { useToast } from "components/Toast";
import {
  parseTags,
  formatTags,
  buildRegistryPayload,
  findDuplicateName,
  filterRegistryRows,
  isActiveRow,
} from "utilities/registry";
import { Plus, Pencil, X, Search, Tag as TagIcon, RotateCcw } from "lucide-react";

/**
 * RegistryEditor (SOUND-672) — the shared list + create/edit surface behind
 * /settings/agents and /settings/banks.
 *
 * Agents and Banks are field-for-field identical (`name`, `status`, `notes`,
 * `tags[]`) and deliberately minimal — copying the 24-field processor document
 * would have created 20 permanently-null columns on each. One component serves
 * both rather than two near-copies that drift apart on the next change.
 *
 * Modelled on FunctionsRegistry (list + modal) rather than the older
 * ProcessorList/ProcessorDetail pair: with four fields, a whole detail route is
 * more chrome than content.
 *
 * The point of the surface is that the CPO maintains these lists WITHOUT
 * engineering — the seed only covers what a Notion export happened to contain.
 */
export default function RegistryEditor({
  title,
  subtitle,
  icon: Icon,
  noun, // singular, lowercase — "agent" / "bank"
  idKey, // "agentId" / "bankId"
  useList, // useAgents / useBanks
  routes, // { create, update, remove }
  createHint,
  emptyDescription,
  tagsHint,
}) {
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const authUser = useMemo(
    () => ({ id: user?.id, accessToken: user?.accessToken }),
    [user?.id, user?.accessToken]
  );

  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const { items, loading, refetch } = useList({ includeInactive: showInactive });
  const [modal, setModal] = useState(null); // null | { mode: "create"|"edit", record? }

  // Belt and braces on the status filter. The API list is documented as
  // active-only by default, but the checkbox is the user's stated intent and
  // this page must not show deactivated rows when it is unticked regardless of
  // what the server sends back.
  const rows = useMemo(
    () => filterRegistryRows(items, { search, showInactive }),
    [items, search, showInactive]
  );

  const hasAny = items.length > 0;

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <button className="btn btn-primary" onClick={() => setModal({ mode: "create" })}>
            <Plus className="h-4 w-4" />
            New {noun}
          </button>
        }
      />

      <div className="container space-y-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className="input pl-9"
              placeholder={`Search ${noun}s, notes or tags…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
        </div>

        {loading ? (
          // Skeleton.Rect puts width/height in an INLINE style, which beats a
          // Tailwind class — the neighbouring settings pages pass
          // `className="h-12 w-full"` and silently render the 120x24 default.
          <div className="space-y-3">
            <Skeleton.Rect width="100%" height={48} />
            <Skeleton.Rect width="100%" height={48} />
            <Skeleton.Rect width="66%" height={48} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Icon}
            heading={
              hasAny || search.trim() ? `No ${noun}s match your filters` : `No ${noun}s yet`
            }
            description={hasAny || search.trim() ? undefined : emptyDescription}
            action={
              hasAny || search.trim() ? undefined : (
                <button className="btn btn-primary" onClick={() => setModal({ mode: "create" })}>
                  <Plus className="h-4 w-4" />
                  New {noun}
                </button>
              )
            }
          />
        ) : (
          <div className="surface overflow-hidden rounded-xl ring-1 ring-border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {["Name", "Tags", "Notes", "Status", ""].map((c) => (
                      <th
                        key={c}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => {
                    const active = isActiveRow(r);
                    const tags = parseTags(r.tags);
                    return (
                      <tr
                        key={r[idKey]}
                        className={`cursor-pointer transition-colors hover:bg-muted ${
                          active ? "" : "opacity-60"
                        }`}
                        onClick={() => setModal({ mode: "edit", record: r })}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-brand/15 text-brand">
                              <Icon className="h-4 w-4" />
                            </span>
                            <span className="truncate font-medium text-foreground">
                              {r.name || <span className="text-muted-foreground">(unnamed)</span>}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {tags.length === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {tags.map((t) => (
                                <span key={t} className="badge badge-muted">
                                  <TagIcon className="h-3 w-3" />
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="max-w-xs px-4 py-3">
                          {r.notes ? (
                            <span className="line-clamp-2 text-muted-foreground" title={r.notes}>
                              {r.notes}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`badge ${active ? "badge-success" : "badge-muted"}`}>
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                active ? "bg-current" : "bg-muted-foreground"
                              }`}
                            />
                            {active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <RegistryModal
          key={modal.mode === "edit" ? modal.record?.[idKey] : "create"}
          mode={modal.mode}
          record={modal.record}
          allRecords={items}
          idKey={idKey}
          noun={noun}
          icon={Icon}
          routes={routes}
          authUser={authUser}
          companyId={companyId}
          createHint={createHint}
          tagsHint={tagsHint}
          toast={toast}
          onClose={() => setModal(null)}
          onSaved={({ deactivated } = {}) => {
            setModal(null);
            // A row you just deactivated should not blink out of existence —
            // reveal inactive rows so it stays on screen, greyed and sunk to
            // the bottom. Flipping the toggle re-runs the fetch by itself, and
            // calling refetch() as well would race a stale active-only request
            // against the new one.
            if (deactivated && !showInactive) setShowInactive(true);
            else refetch();
          }}
        />
      )}
    </div>
  );
}

/**
 * Create/edit modal. Module-level on purpose — a component declared inside a
 * render body gets a new TYPE every render, which React remounts, and shows up
 * as focus loss mid-typing rather than as what it is.
 */
function RegistryModal({
  mode,
  record,
  allRecords,
  idKey,
  noun,
  icon: Icon,
  routes,
  authUser,
  companyId,
  createHint,
  tagsHint,
  toast,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";
  const active = isEdit ? isActiveRow(record) : true;

  const [name, setName] = useState(record?.name || "");
  const [notes, setNotes] = useState(record?.notes || "");
  const [tags, setTags] = useState(formatTags(record?.tags));
  const [submitting, setSubmitting] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  // Escape closes the editor — but not while the deactivate confirmation is up,
  // or one key would dismiss both and the click would be lost.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !confirmDeactivate) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [confirmDeactivate, onClose]);

  const canSubmit = !!name.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    const dup = findDuplicateName(allRecords, name, idKey, record?.[idKey]);
    if (dup) {
      toast({
        status: "warning",
        title: `A ${noun} named “${dup.name}” already exists`,
        description: isActiveRow(dup)
          ? "Edit that record instead of creating a second one."
          : "It is deactivated — tick “Show inactive” and reactivate it instead.",
      });
      return;
    }

    setSubmitting(true);
    try {
      // Status is only ever sent on edit, and only to preserve what the record
      // already has: create defaults active server-side, and deactivation goes
      // through DELETE so the two paths cannot disagree.
      const payload = buildRegistryPayload({
        name,
        notes,
        tags,
        ...(isEdit ? { status: active ? "active" : "inactive" } : {}),
      });
      if (isEdit) {
        await makeApiRequest("put", routes.update(record[idKey]), authUser, companyId, payload);
      } else {
        await makeApiRequest("post", routes.create, authUser, companyId, payload);
      }
      toast({
        status: "success",
        title: isEdit ? `${cap(noun)} updated` : `${cap(noun)} created`,
      });
      onSaved();
    } catch (err) {
      toast({
        status: "destructive",
        title: isEdit ? `Failed to update ${noun}` : `Failed to create ${noun}`,
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // DELETE is a soft-deactivate (status: "inactive"). There is no hard delete on
  // these collections — a record something else already points at must not be
  // able to vanish underneath it.
  const deactivate = async () => {
    setSubmitting(true);
    try {
      await makeApiRequest("delete", routes.remove(record[idKey]), authUser, companyId);
      toast({
        status: "success",
        title: `${cap(noun)} deactivated`,
        description: "It stays in the registry — find it under “Show inactive”.",
      });
      setConfirmDeactivate(false);
      onSaved({ deactivated: true });
    } catch (err) {
      toast({
        status: "destructive",
        title: `Failed to deactivate ${noun}`,
        description: err.response?.data?.error || "Unknown error",
      });
      setSubmitting(false);
    }
  };

  const reactivate = async () => {
    setSubmitting(true);
    try {
      await makeApiRequest("put", routes.update(record[idKey]), authUser, companyId, {
        status: "active",
      });
      toast({ status: "success", title: `${cap(noun)} reactivated` });
      onSaved();
    } catch (err) {
      toast({
        status: "destructive",
        title: `Failed to reactivate ${noun}`,
        description: err.response?.data?.error || "Unknown error",
      });
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
      >
        <div
          className="surface w-full max-w-md rounded-xl p-6 shadow-lg ring-1 ring-border"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-brand/15 text-brand">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {isEdit ? `Edit ${noun}` : `New ${noun}`}
                </h3>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {isEdit ? "Update the name, notes or tags." : createHint}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost h-8 w-8 shrink-0 p-0"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {isEdit && !active && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-border bg-muted/50 px-3 py-2">
              <span className="text-sm text-muted-foreground">
                This {noun} is deactivated and hidden from the default list.
              </span>
              <button
                type="button"
                className="btn btn-secondary shrink-0"
                onClick={reactivate}
                disabled={submitting}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reactivate
              </button>
            </div>
          )}

          <div className="space-y-4">
            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Name</span>
              <input
                autoFocus
                type="text"
                className="input"
                placeholder={noun === "bank" ? "e.g. Evolve" : "e.g. GOAT"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
              />
            </label>

            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">
                Tags <span className="font-normal text-muted-foreground">(optional)</span>
              </span>
              <input
                type="text"
                className="input"
                placeholder="comma, separated"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                onBlur={() => setTags(formatTags(tags))}
              />
              <span className="mt-1 text-xs text-muted-foreground">{tagsHint}</span>
            </label>

            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">
                Notes <span className="font-normal text-muted-foreground">(optional)</span>
              </span>
              <textarea
                className="input min-h-[80px] resize-y"
                placeholder="Anything ops needs to know about this counterparty."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>
          </div>

          <div className="mt-6 flex items-center justify-between gap-2">
            <div>
              {isEdit && active && (
                <button
                  type="button"
                  className="btn btn-ghost text-destructive hover:bg-destructive/10"
                  onClick={() => setConfirmDeactivate(true)}
                  disabled={submitting}
                >
                  Deactivate
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submit}
                disabled={!canSubmit}
              >
                {isEdit ? "Save changes" : `Create ${noun}`}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDeactivate}
        title={`Deactivate ${record?.name || `this ${noun}`}?`}
        description={`It stays in the registry and can be reactivated at any time — nothing already pointing at this ${noun} is broken. It just stops appearing in the default list and in pickers.`}
        confirmLabel="Deactivate"
        variant="destructive"
        confirmDisabled={submitting}
        onConfirm={deactivate}
        onCancel={() => setConfirmDeactivate(false)}
      />
    </>,
    document.body
  );
}

function cap(s) {
  return String(s).charAt(0).toUpperCase() + String(s).slice(1);
}
